import { useCallback, useEffect, useRef, useState } from 'react'
import { useDesignStore } from '../../stores/designStore'
import { useCanvasStore, useCanvasTemporal } from '../../stores/canvasStore'
import { transformOperations } from '../../domain/canvas/value-objects/transform'
import { SelectionBox } from './SelectionBox'
import { SaveIndicator } from '../components/SaveIndicator'
import { calculateCanvasScale, calculateZones } from '../../domain/canvas/services/zoneCalculator'
import { calculateElementBounds } from '../../domain/canvas/services/elementBoundsCalculator'
import { findElementsWithInsufficientBleed } from '../../domain/canvas/services/bleedCoverageService'
import { mmToPx, pxToMm } from '../../domain/shared/units'
import { useFontPreloader } from '../hooks/useFontPreloader'
import { ActionLogger } from '../components/ActionLogger'
import { useHammerGestures } from '../hooks/useHammerGestures'
import { FloatingActionBar } from '../components/FloatingActionBar'
import { RgbColorPicker } from 'react-colorful'
import { useIntlayer } from 'react-intlayer'
import { hexToRgb, rgbToHex } from '../utils/color'
import { DesignStudioFilters } from '@nicelittlethings/types'
import { isDebugPanelEnabled } from '../../shared/flags.ts'

/**
 * Interactive Canvas Component (Refactored)
 * Pure presentation component that delegates all business logic to stores
 */

type InteractiveCanvasProps = {
  isMobile?: boolean
  isDisabled?: boolean
  onTextEdit?: (elementId: string) => void
  onImageReplace?: (elementId: string) => void
  onImageEdit?: (elementId: string) => void
}

export const InteractiveCanvas = ({
  isMobile = false,
  isDisabled = false,
  onTextEdit,
  onImageReplace,
  onImageEdit,
}: InteractiveCanvasProps) => {
  const intl = useIntlayer('interactiveCanvas')
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [copySuccess, setCopySuccess] = useState(false)
  const [jsonExportSuccess, setJsonExportSuccess] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [tempColor, setTempColor] = useState<string>('#000000')
  const [hexInputValue, setHexInputValue] = useState<string>('#000000')
  const [showDebugPanel, setShowDebugPanel] = useState(false)

  // Design store selectors
  const currentDesign = useDesignStore((state) => state.currentDesign)
  const zoneOverrides = useDesignStore((state) => state.zoneOverrides)
  const isSaving = useDesignStore((state) => state.isSaving)
  const lastSaved = useDesignStore((state) => state.lastSaved)
  const saveError = useDesignStore((state) => state.saveError)
  const retryAttempt = useDesignStore((state) => state.retryAttempt)

  const updateZoneOverrides = useDesignStore((state) => state.updateZoneOverrides)
  const resetZoneOverrides = useDesignStore((state) => state.resetZoneOverrides)

  // Canvas store selectors
  const elements = useCanvasStore((state) => state.elements)
  const selectedElementId = useCanvasStore((state) => state.selectedElementId)
  const scale = useCanvasStore((state) => state.scale)
  const debugPanelOpen = useCanvasStore((state) => state.debugPanelOpen)
  const isModalOpen = useCanvasStore((state) => state.isModalOpen)
  const colorHistory = useCanvasStore((state) => state.colorHistory)

  // Load fonts (removed - fonts are now loaded dynamically)
  // const fonts = useQueryFonts()

  // Preload fonts used in the design
  useFontPreloader()

  // Canvas store actions
  const updateElementPosition = useCanvasStore((state) => state.updateElementPosition)
  const updateElementRotation = useCanvasStore((state) => state.updateElementRotation)
  const updateElementSize = useCanvasStore((state) => state.updateElementSize)
  const updateTextFontSize = useCanvasStore((state) => state.updateTextFontSize)
  const updateTextColor = useCanvasStore((state) => state.updateTextColor)
  const updateAssetColor = useCanvasStore((state) => state.updateAssetColor)
  const selectElement = useCanvasStore((state) => state.selectElement)
  const deselectElement = useCanvasStore((state) => state.deselectElement)
  const deleteElement = useCanvasStore((state) => state.deleteElement)
  const startDrag = useCanvasStore((state) => state.startDrag)
  const endDrag = useCanvasStore((state) => state.endDrag)
  const setScale = useCanvasStore((state) => state.setScale)
  const toggleDebugPanel = useCanvasStore((state) => state.toggleDebugPanel)
  const setModalOpen = useCanvasStore((state) => state.setModalOpen)
  const centerElementHorizontally = useCanvasStore((state) => state.centerElementHorizontally)
  const centerElementVertically = useCanvasStore((state) => state.centerElementVertically)
  const centerElementInDesignZone = useCanvasStore((state) => state.centerElementInDesignZone)
  const copyElement = useCanvasStore((state) => state.copyElement)
  const pasteElement = useCanvasStore((state) => state.pasteElement)
  const sendToBack = useCanvasStore((state) => state.sendToBack)
  const flipElementHorizontally = useCanvasStore((state) => state.flipElementHorizontally)

  // Get temporal methods for undo/redo
  const temporal = useCanvasTemporal()

  // Update modal state when color picker opens/closes
  useEffect(() => {
    setModalOpen(showColorPicker)
  }, [showColorPicker, setModalOpen])

  // Initialize temp color when modal opens
  useEffect(() => {
    if (showColorPicker && selectedElementId) {
      const selectedElement = elements[selectedElementId]
      if (selectedElement) {
        if (selectedElement.type === 'text') {
          const currentColor = selectedElement.color || '#000000'
          setTempColor(currentColor)
          setHexInputValue(currentColor)
        } else if (selectedElement.type === 'asset' && selectedElement.assetType === 'engrave') {
          const currentColor = selectedElement.color || '#000000'
          setTempColor(currentColor)
          setHexInputValue(currentColor)
        }
      }
    }
  }, [showColorPicker, selectedElementId, elements])

  useEffect(() => {
    isDebugPanelEnabled().then((value) => {
      setShowDebugPanel(value)
    })
  }, [setShowDebugPanel])

  // State for calculated zones
  const [zones, setZones] = useState<{
    background: { width: number; height: number }
    export: { left: number; top: number; width: number; height: number; clipPath?: string }
    design: { left: number; top: number; width: number; height: number; clipPath?: string }
  }>({
    background: { width: 0, height: 0 },
    export: { left: 0, top: 0, width: 0, height: 0 },
    design: { left: 0, top: 0, width: 0, height: 0 },
  })

  const [backgroundHeightMm, setBackgroundHeightMm] = useState<number>(0)

  // Warn when a photo crosses the trim line but stops inside the bleed band
  const [showBleedTip, setShowBleedTip] = useState(false)
  useEffect(() => {
    if (!zones.export.clipPath) {
      setShowBleedTip(false)
      return
    }
    // Debounced so dragging/resizing doesn't re-measure on every frame
    const timeout = setTimeout(() => {
      const insufficientIds = findElementsWithInsufficientBleed(Object.values(elements), zones)
      setShowBleedTip(insufficientIds.length > 0)
    }, 400)
    return () => clearTimeout(timeout)
  }, [elements, zones])

  // Calculate zones when design or overrides change
  useEffect(() => {
    const loadZones = async () => {
      if (!currentDesign) return

      try {
        const calculatedZones = await calculateZones(
          currentDesign.zones,
          currentDesign.background,
          zoneOverrides,
        )
        setZones(calculatedZones)

        // Calculate height in mm for display
        const heightMm = calculatedZones.background.height / 3.7795275591 // Convert px to mm
        setBackgroundHeightMm(heightMm)
      } catch (error) {
        console.error('Failed to calculate zones:', error)
        // Use default zones based on the design data
        // This ensures we have valid zones even if image loading fails
        const defaultZones = {
          background: {
            width: mmToPx(currentDesign.zones.background.width),
            height: mmToPx(currentDesign.zones.background.width * 1.414), // Default aspect ratio
          },
          export: {
            left: mmToPx(currentDesign.zones.export.left),
            top: mmToPx(currentDesign.zones.export.top),
            width: mmToPx(currentDesign.zones.export.width),
            height: mmToPx(currentDesign.zones.export.height),
          },
          design: {
            left: mmToPx(currentDesign.zones.export.left + currentDesign.zones.design.left),
            top: mmToPx(currentDesign.zones.export.top + currentDesign.zones.design.top),
            width: mmToPx(currentDesign.zones.design.width),
            height: mmToPx(currentDesign.zones.design.height),
          },
        }
        setZones(defaultZones)
        setBackgroundHeightMm(currentDesign.zones.background.width * 1.414)
      }
    }

    loadZones()
  }, [currentDesign, zoneOverrides])

  // Calculate scale on container resize
  useEffect(() => {
    const calculateScale = () => {
      if (
        !containerRef.current ||
        !currentDesign ||
        zones.background.width === 0 ||
        zones.background.height === 0
      ) {
        setScale(1) // Default scale
        return
      }

      const containerWidth = containerRef.current.clientWidth
      const containerHeight = containerRef.current.clientHeight

      // Ensure container has dimensions
      if (containerWidth === 0 || containerHeight === 0) {
        setScale(1)
        return
      }

      const newScale = calculateCanvasScale(
        containerWidth,
        containerHeight,
        zones.background.width,
        zones.background.height,
        isMobile ? 8 : 40,
      )

      // Ensure scale is a valid number
      if (isFinite(newScale) && newScale > 0) {
        setScale(newScale)
      } else {
        setScale(1)
      }
    }

    // Delay initial calculation to ensure DOM is ready
    const timeoutId = setTimeout(calculateScale, 100)

    window.addEventListener('resize', calculateScale)
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', calculateScale)
    }
  }, [currentDesign, zones.background, isMobile, setScale])

  // Deselect element when clicking anywhere on the page (desktop only)
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      // Skip on mobile - touch events are handled differently
      if (isMobile) return

      // Only proceed if there's actually something selected
      if (!selectedElementId) return

      const target = e.target as Node

      // Check if the click is inside the SVG canvas
      if (svgRef.current && svgRef.current.contains(target)) {
        return // Let the SVG handle its own clicks
      }

      // Check if the click is inside an edit panel or edit button
      const editPanel = document.querySelector('[data-edit-panel]')
      const editButton = document.querySelector('[data-edit-button]')
      if (editPanel?.contains(target) || editButton?.contains(target)) {
        return // Don't deselect when clicking on edit UI
      }

      // Click is outside the canvas and edit UI, deselect
      deselectElement()
    }

    document.addEventListener('mousedown', handleGlobalClick)
    return () => document.removeEventListener('mousedown', handleGlobalClick)
  }, [deselectElement, selectedElementId, isMobile])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're typing in an input field
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true'
      ) {
        return // Don't handle shortcuts when typing
      }

      // Check if the asset panel is open and might be handling search
      const assetPanel = document.querySelector('[data-asset-panel]')
      if (assetPanel && !assetPanel.classList.contains('pointer-events-none')) {
        return // Don't handle shortcuts when asset panel is open
      }

      // Delete selected element on Delete or Backspace key
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElementId) {
        e.preventDefault()
        deleteElement(selectedElementId)
      }

      // Copy element (Ctrl/Cmd + C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedElementId) {
        e.preventDefault()
        copyElement(selectedElementId)
      }

      // Paste element (Ctrl/Cmd + V)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault()
        pasteElement()
      }

      // Undo (Ctrl/Cmd + Z)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        temporal.getState().undo()
      }

      // Redo (Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y)
      if (
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')
      ) {
        e.preventDefault()
        temporal.getState().redo()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedElementId, deleteElement, copyElement, pasteElement, temporal])

  // Hammer.js gesture handling for mobile
  const [initialElementState, setInitialElementState] = useState<{
    rotation: number
    scale: number
    fontSize?: number
    width?: number
    height?: number
    position?: { x: number; y: number }
    startScale?: number
    startRotation?: number // Track the initial event.rotation value
  } | null>(null)

  // Track raw touch events
  useEffect(() => {
    if (!isMobile) return

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        // Prevent default for multi-touch to ensure Hammer gets the events
        e.preventDefault()
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        // Prevent default scrolling/zooming on multi-touch
        e.preventDefault()
      }
    }

    const handleTouchEnd = () => {
      // Touch ended
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: false })
      container.addEventListener('touchmove', handleTouchMove, { passive: false })
      container.addEventListener('touchend', handleTouchEnd, { passive: false })
    }

    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart)
        container.removeEventListener('touchmove', handleTouchMove)
        container.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isMobile])

  // Hammer options based on mobile state
  const hammerOptions = isMobile
    ? {
        enable: true,
        domEvents: true,
        touchAction: 'none',
      }
    : undefined

  // Listen for Hammer initialization events
  useEffect(() => {
    const handleHammerInit = () => {
      // Hammer initialized
    }

    const handleHammerError = (e: Event) => {
      const event = e as CustomEvent
      console.error('Hammer error:', event.detail)
    }

    window.addEventListener('hammer-initialized', handleHammerInit)
    window.addEventListener('hammer-error', handleHammerError)

    return () => {
      window.removeEventListener('hammer-initialized', handleHammerInit)
      window.removeEventListener('hammer-error', handleHammerError)
    }
  }, [])

  // Store latest values in refs to avoid stale closures
  const selectedElementIdRef = useRef(selectedElementId)
  const initialElementStateRef = useRef(initialElementState)
  const scaleRef = useRef(scale)
  const zonesRef = useRef(zones)

  useEffect(() => {
    selectedElementIdRef.current = selectedElementId
  }, [selectedElementId])

  useEffect(() => {
    initialElementStateRef.current = initialElementState
  }, [initialElementState])

  useEffect(() => {
    scaleRef.current = scale
  }, [scale])

  useEffect(() => {
    zonesRef.current = zones
  }, [zones])

  // No longer need floating bar position calculation since it's now positioned with CSS

  useHammerGestures(
    containerRef, // Use container instead of separate overlay
    {
      // Remove pan handlers - we'll use mouse events for dragging
      onPinchStart: (event) => {
        const currentSelectedId = selectedElementIdRef.current
        if (!currentSelectedId || !isMobile) {
          return
        }
        const element = elements[currentSelectedId]
        if (!element) return

        if (element.type === 'text') {
          // Get current font size from DOM if available
          const elementInDom = document.querySelector(`[data-element-id="${currentSelectedId}"]`)
          const currentFontSize = elementInDom
            ? parseFloat(window.getComputedStyle(elementInDom).fontSize) / 3.7795275591
            : element.fontSize || 16

          setInitialElementState({
            rotation: element.transform?.rotation || 0,
            scale: 1,
            fontSize: currentFontSize,
            startScale: event.scale || 1,
          })
        } else if (element.type === 'asset' || element.type === 'image') {
          // Get current dimensions from DOM if available
          const elementInDom = document.querySelector(`[data-element-id="${currentSelectedId}"]`)
          let currentWidth = element.dimensions?.width || 50
          let currentHeight = element.dimensions?.height || 50

          if (elementInDom) {
            const bbox = elementInDom.getBoundingClientRect()
            currentWidth = pxToMm(bbox.width / scaleRef.current)
            currentHeight = pxToMm(bbox.height / scaleRef.current)
          }

          setInitialElementState({
            rotation: element.transform?.rotation || 0,
            scale: 1,
            width: currentWidth,
            height: currentHeight,
            startScale: event.scale || 1,
          })
        }
      },
      onPinch: (event) => {
        const currentSelectedId = selectedElementIdRef.current
        const currentInitialState = initialElementStateRef.current

        if (!currentSelectedId || !currentInitialState || !isMobile) return
        const element = elements[currentSelectedId]
        if (!element) return

        // Calculate scale factor from initial scale
        const scaleDelta = event.scale / (currentInitialState.startScale || 1)
        const scaleFactor = scaleDelta

        if (element.type === 'text' && currentInitialState.fontSize) {
          const newFontSize = Math.max(8, Math.min(200, currentInitialState.fontSize * scaleFactor))
          updateTextFontSize(currentSelectedId, newFontSize)
        } else if (
          (element.type === 'asset' || element.type === 'image') &&
          currentInitialState.width &&
          currentInitialState.height
        ) {
          const newWidth = Math.max(1, currentInitialState.width * scaleFactor)
          const newHeight = Math.max(1, currentInitialState.height * scaleFactor)
          updateElementSize(currentSelectedId, { width: newWidth, height: newHeight })
        }
      },
      onPinchEnd: () => {
        setInitialElementState(null)
      },
      onRotateStart: (event) => {
        const currentSelectedId = selectedElementIdRef.current
        if (!currentSelectedId || !isMobile) return
        const element = elements[currentSelectedId]
        if (!element) return

        // Capture CURRENT state (not original) for both rotation and scale
        if (element.type === 'text') {
          // Get current font size from DOM if available
          const elementInDom = document.querySelector(`[data-element-id="${currentSelectedId}"]`)
          const currentFontSize = elementInDom
            ? parseFloat(window.getComputedStyle(elementInDom).fontSize) / 3.7795275591
            : element.fontSize || 16

          setInitialElementState({
            rotation: element.transform?.rotation || 0,
            scale: 1,
            fontSize: currentFontSize,
            startScale: event.scale || 1,
            startRotation: event.rotation || 0, // Store initial finger angle
          })
        } else if (element.type === 'asset' || element.type === 'image') {
          // Get current dimensions from DOM if available
          const elementInDom = document.querySelector(`[data-element-id="${currentSelectedId}"]`)
          let currentWidth = element.dimensions?.width || 50
          let currentHeight = element.dimensions?.height || 50

          if (elementInDom) {
            const bbox = elementInDom.getBoundingClientRect()
            currentWidth = pxToMm(bbox.width / scaleRef.current)
            currentHeight = pxToMm(bbox.height / scaleRef.current)
          }

          setInitialElementState({
            rotation: element.transform?.rotation || 0,
            scale: 1,
            width: currentWidth,
            height: currentHeight,
            startScale: event.scale || 1,
            startRotation: event.rotation || 0, // Store initial finger angle
          })
        }
      },
      onRotate: (event) => {
        const currentSelectedId = selectedElementIdRef.current
        const currentInitialState = initialElementStateRef.current

        if (!currentSelectedId || !currentInitialState || !isMobile) return

        // event.rotation is the angle between fingers
        // Calculate delta from when gesture started
        const rotationDelta = event.rotation - (currentInitialState.startRotation || 0)
        let newRotation = currentInitialState.rotation + rotationDelta

        // Normalize rotation to 0-360 range
        newRotation = ((newRotation % 360) + 360) % 360

        // Snap to 0 degrees if within 5 degrees
        if (newRotation > 355 || newRotation < 5) {
          newRotation = 0
        }

        updateElementRotation(currentSelectedId, newRotation)

        // Also handle scale if it's part of the rotate event (combined gesture)
        if (event.scale && event.scale !== 1 && currentInitialState.startScale !== undefined) {
          const element = elements[currentSelectedId]
          if (!element) return

          // Calculate scale delta from initial scale
          const scaleDelta = event.scale / currentInitialState.startScale

          if (element.type === 'text' && currentInitialState.fontSize) {
            const newFontSize = Math.max(
              8,
              Math.min(200, currentInitialState.fontSize * scaleDelta),
            )
            updateTextFontSize(currentSelectedId, newFontSize)
          } else if (
            (element.type === 'asset' || element.type === 'image') &&
            currentInitialState.width &&
            currentInitialState.height
          ) {
            const newWidth = Math.max(1, currentInitialState.width * scaleDelta)
            const newHeight = Math.max(1, currentInitialState.height * scaleDelta)
            updateElementSize(currentSelectedId, { width: newWidth, height: newHeight })
          }
        }
      },
      onRotateEnd: () => {
        setInitialElementState(null)
      },
      // Remove tap handler - mouse events handle selection
    },
    selectedElementId && isMobile ? hammerOptions : undefined,
  )

  // Handle element mouse/touch down for dragging
  const handleElementMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent, elementId: string) => {
      e.preventDefault()
      e.stopPropagation() // Prevent event bubbling

      // Don't handle element interactions when disabled or modal is open
      if (isDisabled || isModalOpen) return

      // Always handle drag with mouse events (both desktop and mobile)
      // Hammer.js will only handle multi-touch gestures

      // Always select the element immediately, even if already selected
      selectElement(elementId)
      startDrag(elementId)

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

      const element = elements[elementId]
      if (!element) return

      const startX = clientX
      const startY = clientY
      const startPosX = mmToPx(element.position.x)
      const startPosY = mmToPx(element.position.y)

      let hasMoved = false

      const handleMove = (e: MouseEvent | TouchEvent) => {
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

        const deltaX = (clientX - startX) / scale
        const deltaY = (clientY - startY) / scale

        // Only consider it a move if the delta is significant (more than 2 pixels)
        if (!hasMoved && (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2)) {
          hasMoved = true
        }

        if (hasMoved) {
          const newX = pxToMm(startPosX + deltaX)
          const newY = pxToMm(startPosY + deltaY)

          updateElementPosition(elementId, { x: newX, y: newY })
        }
      }

      const handleEnd = () => {
        endDrag()
        document.removeEventListener('mousemove', handleMove)
        document.removeEventListener('mouseup', handleEnd)
        document.removeEventListener('touchmove', handleMove)
        document.removeEventListener('touchend', handleEnd)
      }

      document.addEventListener('mousemove', handleMove, { passive: false })
      document.addEventListener('mouseup', handleEnd)
      document.addEventListener('touchmove', handleMove, { passive: false })
      document.addEventListener('touchend', handleEnd)
    },
    [
      elements,
      scale,
      selectElement,
      startDrag,
      endDrag,
      updateElementPosition,
      isMobile,
      isModalOpen,
      isDisabled,
    ],
  )

  // Handle canvas click/touch to deselect
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      // Don't handle clicks when disabled or modal is open
      if (isDisabled || isModalOpen) return

      // For both mouse and touch, check if we clicked/touched the canvas background
      if (e.target === e.currentTarget && selectedElementId) {
        deselectElement()
      }
    },
    [deselectElement, selectedElementId, isModalOpen, isDisabled],
  )

  // Handle mouse wheel resize for selected element
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!selectedElementId || isModalOpen || isDisabled) return

      // Note: Cannot preventDefault on passive wheel events
      // The browser will handle scrolling normally if no element is selected
      const element = elements[selectedElementId]
      if (!element) return

      // Calculate resize factor based on wheel delta
      const resizeFactor = e.deltaY > 0 ? 0.95 : 1.05

      if (element.type === 'text') {
        const currentFontSize = element.fontSize || 16
        const newFontSize = Math.max(8, Math.min(100, currentFontSize * resizeFactor))
        updateTextFontSize(selectedElementId, newFontSize)
      }
    },
    [selectedElementId, elements, updateTextFontSize, isModalOpen, isDisabled],
  )

  // Handle zone override changes
  const handleZoneOverrideChange = useCallback(
    (zone: 'export' | 'design', field: string, value: number) => {
      if (!currentDesign) return
      const newOverrides = {
        ...zoneOverrides,
        [zone]: {
          ...((zoneOverrides?.[zone] || currentDesign.zones[zone]) as any),
          [field]: value,
        },
      }
      updateZoneOverrides(newOverrides as any)
    },
    [currentDesign, zoneOverrides, updateZoneOverrides],
  )

  // Early return if no design
  if (!currentDesign) {
    return <div className="flex items-center justify-center h-full">{intl.noDesignLoaded}</div>
  }

  return (
    <>
      <SaveIndicator
        isSaving={isSaving}
        lastSaved={lastSaved}
        error={saveError}
        retryAttempt={retryAttempt}
        isMobile={isMobile}
      />

      <div
        className={`w-full h-full bg-white relative ${showDebugPanel ? 'grid' : 'flex'}`}
        style={{
          gridTemplateColumns: showDebugPanel && debugPanelOpen ? '320px 1fr' : '1fr',
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      >
        {/* Blocking overlay when disabled */}
        {isDisabled && (
          <div className="absolute inset-0 z-[60] bg-black bg-opacity-10 cursor-not-allowed" />
        )}
        {/* Debug panel - only visible on localhost and desktop */}
        {showDebugPanel && (
          <div
            className={`bg-gray-900 text-white text-xs overflow-y-auto transition-all duration-300 h-full ${debugPanelOpen ? 'p-4' : 'w-0 p-0 overflow-hidden'}`}
          >
            {debugPanelOpen && (
              <>
                <h3 className="text-sm font-bold mb-3 text-yellow-300">{intl.debugPanel}</h3>

                {/* Design ID with copy button */}
                <div className="mb-4 bg-gray-800 p-3 rounded">
                  <h4 className="font-semibold mb-2 text-gray-300">{intl.designId}</h4>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-2 py-1 bg-gray-700 text-green-400 rounded text-xs font-mono overflow-x-auto">
                      {(currentDesign as any).designId || 'No design ID'}
                    </code>
                    <button
                      onClick={() => {
                        const { designId } = currentDesign as any
                        if (designId) {
                          navigator.clipboard
                            .writeText(designId)
                            .then(() => {
                              setCopySuccess(true)
                              setTimeout(() => setCopySuccess(false), 2000)
                            })
                            .catch((err) => {
                              console.error('Failed to copy:', err)
                            })
                        }
                      }}
                      className={`px-2 py-1 ${
                        copySuccess
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                      } text-white rounded text-xs transition-colors`}
                      title={copySuccess ? intl.copied : 'Copy to clipboard'}
                    >
                      {copySuccess ? (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Action Logger */}
                <ActionLogger />

                {/* ClipPath Status */}
                {zones.design.clipPath && (
                  <div className="mb-4 bg-green-900 p-3 rounded border-2 border-green-500">
                    <h4 className="font-semibold mb-2 text-green-300">⚠️ ClipPath Active</h4>
                    <div className="text-green-200 text-xs space-y-1">
                      <p>Design zone uses custom clip path</p>
                      <details className="cursor-pointer">
                        <summary className="hover:text-green-100">View path data</summary>
                        <code className="block mt-2 p-2 bg-gray-800 rounded text-green-400 break-all">
                          {zones.design.clipPath}
                        </code>
                      </details>
                    </div>
                  </div>
                )}

                <h3 className="text-sm font-bold mb-3 text-yellow-300">{intl.zoneEditor}</h3>
                {/* Zone editors */}
                {/* Background zone editor */}
                <div className="mb-4 bg-gray-800 p-3 rounded">
                  <h4 className="font-semibold mb-2 text-gray-300">{intl.backgroundZone}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-gray-400 mb-1">{intl.width}</label>
                      <input
                        type="number"
                        value={
                          zoneOverrides?.background?.width || currentDesign.zones.background.width
                        }
                        onChange={(e) => {
                          const newWidth = parseFloat(e.target.value) || 0

                          const newOverrides = {
                            ...zoneOverrides,
                            background: {
                              width: newWidth,
                            },
                          }
                          updateZoneOverrides(newOverrides as any)
                        }}
                        className="w-full px-2 py-1 bg-gray-700 text-white rounded"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-1">{intl.height}</label>
                      <input
                        type="number"
                        value={backgroundHeightMm.toFixed(1)}
                        readOnly
                        className="w-full px-2 py-1 bg-gray-800 text-gray-400 rounded cursor-not-allowed"
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>

                {/* Export and Design zone editors */}
                {(['export', 'design'] as const).map((zoneType) => {
                  const zone = zoneOverrides?.[zoneType] || currentDesign.zones[zoneType]
                  return (
                    <div key={zoneType} className="mb-4 bg-gray-800 p-3 rounded">
                      <h4
                        className={`font-semibold mb-2 ${zoneType === 'export' ? 'text-green-300' : 'text-blue-300'}`}
                      >
                        {zoneType === 'export'
                          ? intl.exportZone
                          : zoneType === 'design'
                            ? intl.designZone
                            : intl.backgroundZone}
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {['left', 'top', 'width', 'height'].map((field) => (
                          <div key={field}>
                            <label className="block text-gray-400 mb-1">
                              {field === 'width'
                                ? intl.width
                                : field === 'height'
                                  ? intl.height
                                  : field === 'left'
                                    ? intl.left
                                    : intl.top}
                            </label>
                            <input
                              type="number"
                              value={zone[field as keyof typeof zone]}
                              onChange={(e) =>
                                handleZoneOverrideChange(
                                  zoneType,
                                  field,
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              className="w-full px-2 py-1 bg-gray-700 text-white rounded"
                              step="0.1"
                            />
                          </div>
                        ))}
                      </div>
                      {/* Center Position for export zone only */}
                      {zoneType === 'export' && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <label className="block text-gray-400 mb-2 text-xs">
                            Center Position (mm offset in export zone)
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-gray-500 mb-1 text-xs">X</label>
                              <input
                                type="number"
                                value={zone.centerPosition?.x ?? zone.width / 2}
                                onChange={(e) => {
                                  const newX = parseFloat(e.target.value) || 0
                                  const newOverrides = {
                                    ...zoneOverrides,
                                    export: {
                                      ...(zoneOverrides?.export || currentDesign.zones.export),
                                      centerPosition: {
                                        x: newX,
                                        y:
                                          zone.centerPosition?.y ??
                                          (zoneOverrides?.export || currentDesign.zones.export)
                                            .height / 2,
                                      },
                                    },
                                  }
                                  updateZoneOverrides(newOverrides as any)
                                }}
                                className="w-full px-2 py-1 bg-gray-700 text-white rounded text-xs"
                                step="0.1"
                              />
                            </div>
                            <div>
                              <label className="block text-gray-500 mb-1 text-xs">Y</label>
                              <input
                                type="number"
                                value={zone.centerPosition?.y ?? zone.height / 2}
                                onChange={(e) => {
                                  const newY = parseFloat(e.target.value) || 0
                                  const newOverrides = {
                                    ...zoneOverrides,
                                    export: {
                                      ...(zoneOverrides?.export || currentDesign.zones.export),
                                      centerPosition: {
                                        x:
                                          zone.centerPosition?.x ??
                                          (zoneOverrides?.export || currentDesign.zones.export)
                                            .width / 2,
                                        y: newY,
                                      },
                                    },
                                  }
                                  updateZoneOverrides(newOverrides as any)
                                }}
                                className="w-full px-2 py-1 bg-gray-700 text-white rounded text-xs"
                                step="0.1"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Default: ({(zone.width / 2).toFixed(1)}, {(zone.height / 2).toFixed(1)})
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* Reset button */}
                <button
                  onClick={resetZoneOverrides}
                  className="w-full mb-4 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                >
                  {intl.resetToOriginal}
                </button>

                {/* Export JSON button */}
                <button
                  onClick={() => {
                    // Convert elements to API format
                    const elementsArray = Object.values(elements).map((el: any) => {
                      const baseElement = {
                        elementId: el.id,
                        type: el.type,
                        centerPosition: {
                          left: el.position.x,
                          top: el.position.y,
                        },
                        rotation: el.transform?.rotation || 0,
                        // Include transform string for scale/skew data (excluding rotation which is stored separately)
                        transform: el.transform
                          ? transformOperations.toCSSString(el.transform)
                          : undefined,
                      }

                      if (el.type === 'text') {
                        return {
                          ...baseElement,
                          value: {
                            nl: el.content,
                            fr: el.content,
                            de: el.content,
                            en: el.content,
                          },
                          fontSize: el.fontSize,
                          fontFamily: el.fontFamily,
                          fontColor: el.color,
                          fontWeight: el.fontWeight,
                          fontItalic: el.fontItalic,
                        }
                      } else if (el.type === 'asset') {
                        return {
                          ...baseElement,
                          assetId: el.assetId,
                          assetType: el.assetType,
                          color: el.color,
                        }
                      } else if (el.type === 'image') {
                        return {
                          ...baseElement,
                          value: el.src,
                          borderRadius: el.borderRadius,
                          filters: el.filters,
                        }
                      }

                      return baseElement
                    })

                    // Export in the API format
                    const exportData = {
                      elements: elementsArray,
                    }

                    const jsonData = JSON.stringify(exportData, null, 2)
                    navigator.clipboard.writeText(jsonData).then(() => {
                      // Show a temporary success message
                      setJsonExportSuccess(true)
                      setTimeout(() => setJsonExportSuccess(false), 2000)
                    })
                  }}
                  className="w-full mb-4 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors relative"
                >
                  {jsonExportSuccess ? intl.copied : intl.exportJson}
                </button>

                {/* Current values display */}
                <div className="text-xs space-y-2 border-t border-gray-700 pt-3">
                  <div className="text-gray-400">
                    Background: {currentDesign.zones.background.width}mm (width)
                  </div>
                  <div className="text-gray-400">Scale: {scale.toFixed(3)}</div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Toggle button for debug panel */}
        {showDebugPanel && (
          <button
            onClick={toggleDebugPanel}
            className="fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-gray-800 text-white p-2 rounded-r-md hover:bg-gray-700 transition-colors"
            style={{ left: debugPanelOpen ? '320px' : '0' }}
          >
            {debugPanelOpen ? '◀' : '▶'}
          </button>
        )}

        {/* Main editor area */}
        <div
          ref={containerRef}
          id="design-studio-canvas"
          className={`relative flex items-center justify-center overflow-hidden ${showDebugPanel ? 'p-5' : 'p-2'} flex-1`}
          style={{
            touchAction: 'none',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
        >
          {/* Bleed coverage tip */}
          {showBleedTip && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 max-w-md pointer-events-none rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 shadow-md">
              💡 {intl.bleedCoverageTip}
            </div>
          )}
          <div
            className="relative bg-white"
            style={{
              width: !isFinite(zones.background.width * scale) ? 0 : zones.background.width * scale,
              height: !isFinite(zones.background.height * scale)
                ? 0
                : zones.background.height * scale,
            }}
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${currentDesign.background})` }}
            />

            {/* SVG Canvas */}
            <svg
              ref={svgRef}
              className="absolute inset-0 cursor-default canvas-background"
              viewBox={`0 0 ${zones.background.width} ${zones.background.height}`}
              onClick={handleCanvasClick}
              onTouchStart={handleCanvasClick}
              onWheel={handleWheel}
            >
              {/* Background zone for testing */}
              <rect
                data-zone="background"
                x={0}
                y={0}
                width={zones.background.width}
                height={zones.background.height}
                fill="transparent"
                style={{ pointerEvents: 'none' }}
              />
              <defs>
                <clipPath id="design-zone-clip">
                  {zones.design.clipPath ? (
                    <path data-zone="design" d={zones.design.clipPath} pointerEvents="none" />
                  ) : (
                    <rect
                      data-zone="design"
                      x={zones.design.left}
                      y={zones.design.top}
                      width={zones.design.width}
                      height={zones.design.height}
                      pointerEvents="none"
                    />
                  )}
                </clipPath>
                {/* Bleed contour: print extends to here and is trimmed at the design clipPath */}
                {zones.export.clipPath && (
                  <clipPath id="bleed-zone-clip">
                    <path data-zone="bleed" d={zones.export.clipPath} pointerEvents="none" />
                  </clipPath>
                )}
                {/* Image filters - only filters go in defs, clipPaths are local to each element */}
                {Object.values(elements)
                  .filter((el) => el.type === 'image')
                  .map((element) => {
                    if (element.type !== 'image') return null

                    const hasFilters =
                      element.filters && DesignStudioFilters.hasActiveFilters(element.filters)
                    const filterId = hasFilters ? element.id : undefined
                    const filterSVG = hasFilters
                      ? DesignStudioFilters.generateSVGFilter(filterId!, element.filters!)
                      : ''

                    return filterSVG ? (
                      <g key={element.id} dangerouslySetInnerHTML={{ __html: filterSVG }} />
                    ) : null
                  })}
              </defs>

              {/* Export zone - only visible when debug panel is open AND on localhost */}
              {showDebugPanel && debugPanelOpen && (
                <>
                  <rect
                    data-zone="export"
                    x={zones.export.left}
                    y={zones.export.top}
                    width={zones.export.width}
                    height={zones.export.height}
                    fill="white"
                    fillOpacity="0.2"
                  />
                  <rect
                    x={zones.export.left}
                    y={zones.export.top}
                    width={zones.export.width}
                    height={zones.export.height}
                    fill="none"
                    stroke="red"
                    strokeWidth={2 / scale}
                    strokeOpacity="0.8"
                    style={{
                      filter: 'drop-shadow(0 0 3px rgba(255, 0, 0, 0.3))',
                    }}
                  />
                  {/* Bleed contour (export clipPath) */}
                  {zones.export.clipPath && (
                    <path
                      d={zones.export.clipPath}
                      fill="none"
                      stroke="rgba(0,150,255,0.9)"
                      strokeWidth={2 / scale}
                      strokeDasharray={`${6 / scale},${3 / scale}`}
                      style={{
                        filter: 'drop-shadow(0 0 3px rgba(0, 150, 255, 0.5))',
                      }}
                    />
                  )}
                </>
              )}

              {/* Design zone outline - only visible when debug panel is open AND on localhost */}
              {showDebugPanel && debugPanelOpen && (
                <>
                  {/* Show clipPath shape if present, otherwise show rectangle */}
                  {zones.design.clipPath ? (
                    <>
                      {/* Filled background to show clipPath area clearly */}
                      <path
                        d={zones.design.clipPath}
                        fill="rgba(0,255,100,0.15)"
                        stroke="rgba(0,255,100,0.9)"
                        strokeWidth={3 / scale}
                        style={{
                          filter: 'drop-shadow(0 0 4px rgba(0, 255, 100, 0.8))',
                        }}
                      />
                      {/* Also show the bounding box for reference */}
                      <rect
                        x={zones.design.left}
                        y={zones.design.top}
                        width={zones.design.width}
                        height={zones.design.height}
                        fill="none"
                        stroke="rgba(255,100,0,0.4)"
                        strokeWidth={1 / scale}
                        strokeDasharray={`${3 / scale},${3 / scale}`}
                      />
                      {/* Label indicating clipPath is active */}
                      <text
                        x={zones.design.left + 5}
                        y={zones.design.top - 5}
                        fill="rgba(0,255,100,0.9)"
                        fontSize={14 / scale}
                        fontWeight="bold"
                      >
                        CLIPPATH ACTIVE
                      </text>
                    </>
                  ) : (
                    <rect
                      x={zones.design.left}
                      y={zones.design.top}
                      width={zones.design.width}
                      height={zones.design.height}
                      fill="none"
                      stroke="rgba(0,100,255,0.8)"
                      strokeWidth={2 / scale}
                      strokeDasharray={`${5 / scale},${5 / scale}`}
                      style={{
                        filter: 'drop-shadow(0 0 2px rgba(0, 100, 255, 0.5))',
                      }}
                    />
                  )}
                  {/* Center position indicator - blue dot */}
                  {(() => {
                    const exportZone = zoneOverrides?.export || currentDesign.zones.export
                    const centerX = exportZone.centerPosition?.x ?? exportZone.width / 2
                    const centerY = exportZone.centerPosition?.y ?? exportZone.height / 2
                    const absoluteCenterX = zones.export.left + mmToPx(centerX)
                    const absoluteCenterY = zones.export.top + mmToPx(centerY)
                    const dotRadius = 6 / scale // 6px visual radius

                    return (
                      <circle
                        cx={absoluteCenterX}
                        cy={absoluteCenterY}
                        r={dotRadius}
                        fill="rgba(0, 100, 255, 0.8)"
                        stroke="white"
                        strokeWidth={2 / scale}
                        style={{
                          filter: 'drop-shadow(0 0 3px rgba(0, 100, 255, 0.8))',
                        }}
                      />
                    )
                  })()}
                </>
              )}

              {/* Design elements - sorted by zIndex, then by type */}
              {/* Clip to the bleed contour when present so customers see their photo extends into the trimmed margin */}
              <g
                clipPath={`url(#${zones.export.clipPath ? 'bleed-zone-clip' : 'design-zone-clip'})`}
              >
                {Object.values(elements)
                  .sort((a, b) => {
                    // First sort by zIndex if they have it
                    const aZIndex = a.zIndex || 0
                    const bZIndex = b.zIndex || 0
                    if (aZIndex !== bZIndex) {
                      return aZIndex - bZIndex
                    }
                    // If zIndex is the same, fall back to type-based layering
                    const layerOrder = { image: 0, asset: 1, text: 2, shape: 1 }
                    const aLayer = layerOrder[a.type] ?? 1
                    const bLayer = layerOrder[b.type] ?? 1
                    return aLayer - bLayer
                  })
                  .map((element) => {
                    // Position elements relative to export zone (design zone is just for clipping)
                    const x = zones.export.left + mmToPx(element.position.x)
                    const y = zones.export.top + mmToPx(element.position.y)
                    const rotation = element.transform?.rotation || 0

                    if (element.type === 'text') {
                      return (
                        <g key={element.id} transform={`rotate(${rotation} ${x} ${y})`}>
                          <text
                            data-element-id={element.id}
                            x={x}
                            y={y}
                            fontFamily={element.fontFamily || 'Arial'}
                            fontSize={mmToPx(element.fontSize || 16)}
                            fontWeight={
                              // Migration: convert old string values to numbers
                              typeof element.fontWeight === 'string'
                                ? element.fontWeight === 'bold'
                                  ? 700
                                  : 400
                                : element.fontWeight || 400
                            }
                            fontStyle={
                              // Handle both old fontStyle and new fontItalic
                              element.fontItalic || (element as any).fontStyle === 'italic'
                                ? 'italic'
                                : 'normal'
                            }
                            fill={element.color || '#000000'}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="cursor-move select-none"
                            onMouseDown={(e) => handleElementMouseDown(e, element.id)}
                            onTouchStart={(e) => handleElementMouseDown(e, element.id)}
                          >
                            {(() => {
                              const lines = (element.content || '').split('\n')
                              const lineHeight = 1.2 // Standard line height multiplier
                              const fontSize = mmToPx(element.fontSize || 16)
                              const totalLines = lines.length
                              const startY = (-(totalLines - 1) * fontSize * lineHeight) / 2

                              return lines.map((line, index) => (
                                <tspan
                                  key={index}
                                  x={x}
                                  dy={index === 0 ? startY : fontSize * lineHeight}
                                >
                                  {line || '\u00A0'}
                                </tspan>
                              ))
                            })()}
                          </text>
                        </g>
                      )
                    } else if (element.type === 'asset') {
                      const baseWidth = mmToPx(element.dimensions?.width || 50)
                      const baseHeight = mmToPx(element.dimensions?.height || 50)
                      const scaleX = element.transform?.scaleX || 1
                      const scaleY = element.transform?.scaleY || 1
                      const absScaleX = Math.abs(scaleX)
                      const absScaleY = Math.abs(scaleY)
                      const width = baseWidth * absScaleX
                      const height = baseHeight * absScaleY

                      // Adjust x,y to position by center (SVG images are positioned by top-left)
                      const imageX = x - width / 2
                      const imageY = y - height / 2

                      // For engrave assets with color, we need to apply a filter
                      const needsColorFilter = element.assetType === 'engrave' && element.color
                      const filterId = needsColorFilter ? `color-filter-${element.id}` : undefined

                      // Build transform string: rotate + scale (for flip)
                      const transforms = [`rotate(${rotation} ${x} ${y})`]
                      if (scaleX < 0 || scaleY < 0) {
                        transforms.push(`translate(${x} ${y})`)
                        transforms.push(`scale(${scaleX < 0 ? -1 : 1}, ${scaleY < 0 ? -1 : 1})`)
                        transforms.push(`translate(${-x} ${-y})`)
                      }
                      const combinedTransform = transforms.join(' ')

                      return (
                        <g key={element.id} transform={combinedTransform}>
                          {/* Define color filter for engrave assets */}
                          {needsColorFilter && (
                            <defs>
                              <filter id={filterId}>
                                {/* Flood with the target color */}
                                <feFlood floodColor={element.color || '#000000'} />
                                {/* Use the source graphic's alpha channel */}
                                <feComposite in2="SourceAlpha" operator="in" />
                              </filter>
                            </defs>
                          )}
                          {element.src ? (
                            <image
                              data-element-id={element.id}
                              x={imageX}
                              y={imageY}
                              width={width}
                              height={height}
                              xlinkHref={element.src}
                              href={element.src}
                              className="cursor-move"
                              onMouseDown={(e) => handleElementMouseDown(e, element.id)}
                              onTouchStart={(e) => handleElementMouseDown(e, element.id)}
                              preserveAspectRatio="xMidYMid meet"
                              filter={filterId ? `url(#${filterId})` : undefined}
                            />
                          ) : (
                            <>
                              <rect
                                data-element-id={element.id}
                                x={imageX}
                                y={imageY}
                                width={width}
                                height={height}
                                fill="rgba(200, 200, 200, 0.5)"
                                stroke="rgba(150, 150, 150, 0.8)"
                                strokeWidth={1 / scale}
                                className="cursor-move"
                                onMouseDown={(e) => handleElementMouseDown(e, element.id)}
                                onTouchStart={(e) => handleElementMouseDown(e, element.id)}
                              />
                              <text
                                x={x}
                                y={y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="12"
                                fill="gray"
                                pointerEvents="none"
                              >
                                {intl.photoPlaceholder}
                              </text>
                            </>
                          )}
                        </g>
                      )
                    } else if (element.type === 'image') {
                      // Handle user-uploaded images
                      const baseWidth = mmToPx(element.dimensions?.width || 50)
                      const baseHeight = mmToPx(element.dimensions?.height || 50)
                      const scaleX = element.transform?.scaleX || 1
                      const scaleY = element.transform?.scaleY || 1
                      const width = baseWidth * scaleX
                      const height = baseHeight * scaleY

                      // Adjust x,y to position by center (SVG images are positioned by top-left)
                      const imageX = x - width / 2
                      const imageY = y - height / 2

                      // Calculate border radius if present
                      const baseDimWidth = element.dimensions?.width || 50
                      const baseDimHeight = element.dimensions?.height || 50
                      const rx =
                        element.borderRadius && element.borderRadius > 0
                          ? mmToPx(
                              DesignStudioFilters.calculateBorderRadius(
                                element.borderRadius,
                                baseDimWidth,
                                baseDimHeight,
                              ),
                            ) *
                            ((scaleX + scaleY) / 2)
                          : 0

                      // clipPath and filter IDs for this image
                      const clipPathId =
                        element.borderRadius && element.borderRadius > 0
                          ? `image-clip-${element.id}`
                          : undefined
                      const hasFilters =
                        element.filters && DesignStudioFilters.hasActiveFilters(element.filters)
                      const filterId = hasFilters ? `filter-${element.id}` : undefined

                      return (
                        <g key={element.id} transform={`rotate(${rotation} ${x} ${y})`}>
                          {/* Define clipPath locally within this element's group */}
                          {clipPathId && (
                            <defs>
                              <clipPath id={clipPathId}>
                                <rect
                                  x={imageX}
                                  y={imageY}
                                  width={width}
                                  height={height}
                                  rx={rx}
                                  ry={rx}
                                />
                              </clipPath>
                            </defs>
                          )}
                          {element.src ? (
                            <image
                              data-element-id={element.id}
                              x={imageX}
                              y={imageY}
                              width={width}
                              height={height}
                              xlinkHref={element.src}
                              href={element.src}
                              className="cursor-move"
                              onMouseDown={(e) => handleElementMouseDown(e, element.id)}
                              onTouchStart={(e) => handleElementMouseDown(e, element.id)}
                              preserveAspectRatio="xMidYMid meet"
                              clipPath={clipPathId ? `url(#${clipPathId})` : undefined}
                              filter={filterId ? `url(#${filterId})` : undefined}
                            />
                          ) : (
                            <>
                              <rect
                                data-element-id={element.id}
                                x={imageX}
                                y={imageY}
                                width={width}
                                height={height}
                                fill="rgba(200, 200, 200, 0.5)"
                                stroke="rgba(150, 150, 150, 0.8)"
                                strokeWidth={1 / scale}
                                className="cursor-move"
                                onMouseDown={(e) => handleElementMouseDown(e, element.id)}
                                onTouchStart={(e) => handleElementMouseDown(e, element.id)}
                              />
                              <text
                                x={x}
                                y={y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="12"
                                fill="gray"
                                pointerEvents="none"
                              >
                                {intl.userPhotoPlaceholder}
                              </text>
                            </>
                          )}
                        </g>
                      )
                    }
                    return null
                  })}
              </g>

              {/* Bleed overlay: fade the margin between the trim line (design clipPath) and the
                  bleed contour (export clipPath) - that strip is printed but cut off.
                  A mask (bleed white, trim black) isolates the band; paths are kept separate
                  because concatenating them would reinterpret a leading relative moveto. */}
              {zones.export.clipPath &&
                (() => {
                  const trimPath =
                    zones.design.clipPath ||
                    `M ${zones.design.left},${zones.design.top} h ${zones.design.width} v ${zones.design.height} h ${-zones.design.width} Z`

                  return (
                    <g data-bleed-overlay="true" pointerEvents="none">
                      <mask id="bleed-band-mask">
                        <path d={zones.export.clipPath} fill="white" />
                        <path d={trimPath} fill="black" />
                      </mask>
                      <rect
                        x={0}
                        y={0}
                        width={zones.background.width}
                        height={zones.background.height}
                        fill="white"
                        fillOpacity={0.6}
                        mask="url(#bleed-band-mask)"
                      />
                      {/* Dashed trim line: everything outside this line is cut off */}
                      <path
                        d={trimPath}
                        fill="none"
                        stroke="rgba(0, 0, 0, 0.4)"
                        strokeWidth={1 / scale}
                        strokeDasharray={`${4 / scale},${4 / scale}`}
                      />
                    </g>
                  )
                })()}
            </svg>

            {/* Selection overlay - separate SVG that can extend beyond bounds */}
            {selectedElementId && elements[selectedElementId] && (
              <svg
                className="absolute inset-0 pointer-events-none"
                viewBox={`0 0 ${zones.background.width} ${zones.background.height}`}
                style={{
                  overflow: 'visible', // Allow selection box to extend beyond SVG bounds
                }}
              >
                <g style={{ pointerEvents: 'auto' }}>
                  {(() => {
                    const selectedElement = elements[selectedElementId]
                    const bounds = calculateElementBounds(selectedElement, zones.export)

                    // For text elements, the rotation center is the text position (baseline center)
                    const rotationCenterX =
                      selectedElement.type === 'text'
                        ? zones.export.left + mmToPx(selectedElement.position.x)
                        : undefined
                    const rotationCenterY =
                      selectedElement.type === 'text'
                        ? zones.export.top + mmToPx(selectedElement.position.y)
                        : undefined

                    return (
                      <SelectionBox
                        x={bounds.x}
                        y={bounds.y}
                        width={bounds.width}
                        height={bounds.height}
                        rotation={selectedElement.transform?.rotation || 0}
                        rotationCenterX={rotationCenterX}
                        rotationCenterY={rotationCenterY}
                        scale={scale}
                        elementId={selectedElementId}
                        onRotate={(angle) => updateElementRotation(selectedElementId, angle)}
                        onResize={(width, height) => {
                          if (selectedElement.type === 'text') {
                            const newFontSize = pxToMm(height)
                            const clampedFontSize = Math.max(8, Math.min(100, newFontSize))
                            updateTextFontSize(selectedElementId, clampedFontSize)
                          } else if (
                            selectedElement.type === 'asset' ||
                            selectedElement.type === 'image'
                          ) {
                            // Convert pixel dimensions back to mm
                            const newWidth = pxToMm(width)
                            const newHeight = pxToMm(height)

                            // Update the element dimensions
                            updateElementSize(selectedElementId, {
                              width: Math.max(1, newWidth), // Minimum 1mm
                              height: Math.max(1, newHeight), // Minimum 1mm
                            })
                          }
                        }}
                      />
                    )
                  })()}
                </g>
              </svg>
            )}
          </div>

          {/* Floating Action Bar - only show when element is selected */}
          {selectedElementId &&
            (() => {
              const selectedElement = elements[selectedElementId]
              const isPrintDesign = currentDesign?.type === 'print'
              const isTextElement = selectedElement?.type === 'text'
              const isAssetElement = selectedElement?.type === 'asset'
              const isEngraveAsset =
                selectedElement?.type === 'asset' && selectedElement.assetType === 'engrave'
              const isImageElement = selectedElement?.type === 'image'
              const showColorButton = isPrintDesign && (isTextElement || isEngraveAsset) // Show color for text and engrave assets in print designs
              const showFlipHorizontalButton = isAssetElement // Show flip horizontal for all assets
              const showTextEditButton = isTextElement
              const showImageReplaceButton = isImageElement
              const showImageEditButton = isImageElement

              return (
                <FloatingActionBar
                  onHorizontalCenter={() => centerElementHorizontally(selectedElementId)}
                  onVerticalCenter={() => centerElementVertically(selectedElementId)}
                  onCenterInDesign={() => centerElementInDesignZone(selectedElementId)}
                  onDelete={() => deleteElement(selectedElementId)}
                  onSendToBack={() => sendToBack(selectedElementId)}
                  onFlipHorizontal={() => flipElementHorizontally(selectedElementId)}
                  showFlipHorizontalButton={showFlipHorizontalButton}
                  onColorChange={() => setShowColorPicker(true)}
                  showColorButton={showColorButton}
                  onTextEdit={() => onTextEdit?.(selectedElementId)}
                  showTextEditButton={showTextEditButton}
                  onImageReplace={() => onImageReplace?.(selectedElementId)}
                  showImageReplaceButton={showImageReplaceButton}
                  onImageEdit={() => onImageEdit?.(selectedElementId)}
                  showImageEditButton={showImageEditButton}
                  className="floating-action-bar absolute bottom-4 left-1/2 -translate-x-1/2"
                />
              )
            })()}
        </div>
      </div>

      {/* Color Picker Modal - moved outside of container */}
      {showColorPicker &&
        selectedElementId &&
        (() => {
          const selectedElement = elements[selectedElementId]
          const isTextElement = selectedElement?.type === 'text'
          const isEngraveAsset =
            selectedElement?.type === 'asset' && selectedElement.assetType === 'engrave'

          if (!selectedElement || (!isTextElement && !isEngraveAsset)) return null

          const handleApply = () => {
            if (isTextElement) {
              updateTextColor(selectedElementId, tempColor)
            } else if (isEngraveAsset) {
              updateAssetColor(selectedElementId, tempColor)
            }
            setShowColorPicker(false)
          }

          const handleCancel = () => {
            setShowColorPicker(false)
          }

          const isValidHex = (hex: string): boolean => {
            return /^#?([a-f\d]{6})$/i.test(hex)
          }

          const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target
            setHexInputValue(value)

            // Only update color if valid hex
            if (isValidHex(value)) {
              const normalizedHex = value.startsWith('#') ? value : `#${value}`
              setTempColor(normalizedHex)
            }
          }

          return (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
              onMouseDown={(e) => {
                // Only close if clicking directly on backdrop
                if (e.target === e.currentTarget) {
                  handleCancel()
                }
              }}
            >
              <div
                className="bg-white rounded-lg p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold mb-4">
                  {isTextElement ? intl.textColor : intl.assetColor}
                </h3>
                <div className="mb-4">
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <RgbColorPicker
                      color={hexToRgb(tempColor)}
                      onChange={(rgb) => {
                        const hexColor = rgbToHex(rgb)
                        setTempColor(hexColor)
                        setHexInputValue(hexColor)
                      }}
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <div
                      className="w-10 h-10 rounded border border-gray-300 flex-shrink-0"
                      style={{ backgroundColor: tempColor }}
                    />
                    <input
                      type="text"
                      value={hexInputValue}
                      onChange={handleHexInputChange}
                      placeholder="#RRGGBB"
                      className="flex-1 px-2 py-1 text-sm font-mono border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      maxLength={7}
                    />
                  </div>

                  {/* Color History */}
                  {colorHistory.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs text-gray-500 mb-2">{intl.recentColors}</div>
                      <div className="flex gap-2">
                        {colorHistory.map((color, index) => (
                          <button
                            key={`${color}-${index}`}
                            onClick={() => {
                              setTempColor(color)
                              setHexInputValue(color)
                            }}
                            className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-400 transition-colors"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleApply}
                    className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                  >
                    {intl.apply}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                  >
                    {intl.cancel}
                  </button>
                </div>
              </div>
            </div>
          )
        })()}
    </>
  )
}
